import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    document.addEventListener('click', this.#onARSelectorClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#onARSelectorClick);
  }

  #onARSelectorClick = (event) => {
    const trigger = event.target.closest('#ARselector');
    if (!trigger) return;

    event.preventDefault();

    if (!this.isOpen) {
    this.refs.details.setAttribute('open', '');
    this.open();
  } else { 
    this.refs.details.removeAttribute('open');  
    this.close();
  }
  };

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {

    const headerDrawer = document.querySelector('.header__drawer');
    const imgOpen = document.querySelector('.menu-burger-selector-opener');
    const imgClose = document.querySelector('.menu-burger-selector-closer');
    if (!headerDrawer) {
      console.warn('[HeaderDrawer] .header__drawer NOT FOUND');
    } else {
      console.log('[HeaderDrawer] hiding .header__drawer');
      headerDrawer.style.display = 'flex';
      imgOpen.style.display = 'none';
      imgClose.style.display = 'block';
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) {
      console.warn('[HeaderDrawer] summary not found');
      return;
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const animatedDrawer =
        details.querySelector('.menu-drawer, .menu-drawer__submenu');

      onAnimationEnd(
        animatedDrawer || details,
        () => {
          console.log('[HeaderDrawer] trapFocus activated');
          trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    console.log('[HeaderDrawer] close() called');

    const headerDrawer = document.querySelector('.header__drawer');
    const imgOpen = document.querySelector('.menu-burger-selector-opener');
    const imgClose = document.querySelector('.menu-burger-selector-closer');
    if (!headerDrawer) {
      console.warn('[HeaderDrawer] .header__drawer NOT FOUND');
    } else {
      console.log('[HeaderDrawer] showing .header__drawer');
      headerDrawer.style.display = 'none';
      imgOpen.style.display = 'block';
      imgClose.style.display = 'none';
    }

    const summary = details.querySelector('summary');
    if (!summary) {
      console.warn('[HeaderDrawer] summary not found on close');
      return;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const animatedDrawer =
      details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      animatedDrawer || details,
      () => {
        console.log('[HeaderDrawer] animation end → reset');

        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}